'use client';

import isPropValid from '@emotion/is-prop-valid';
import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';
import styled from 'styled-components';

import type { SpacingType } from '@zoomit/primitives';
import { spacingHelper } from '@zoomit/primitives';

import { typographyVariant } from '../../constants/typography';
import { cssVar } from '../../css/css-var';
import { theme } from '../../theme';

type TagType = 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'span' | 'div' | 'sub';

type Props = {
  variant?: keyof typeof typographyVariant;
  textColor?: string;
  fontWeight?: CSSProperties['fontWeight'];
  fontSize?: number;
  tag?: TagType;
  lineHeight?: CSSProperties['lineHeight'];
  spacing?: SpacingType;
  textAlign?: CSSProperties['textAlign'];
};

type TypographyProps = Props & HTMLAttributes<HTMLHeadingElement>;

export const Typography = (props: TypographyProps) => {
  const {
    variant = 'body',
    textColor = cssVar(theme.text),
    fontSize,
    fontWeight,
    lineHeight,
    textAlign,
    spacing,
    children,
    ...rest
  } = props;
  const tag = (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].some((i) => i === variant) ? variant : 'span') as TagType;

  return (
    <DynamicTypography
      tag={tag}
      variant={variant}
      spacing={spacing}
      textColor={textColor}
      fontWeight={fontWeight}
      fontSize={fontSize}
      lineHeight={lineHeight}
      textAlign={textAlign}
      {...rest}
    >
      {children}
    </DynamicTypography>
  );
};

type StyledDynamicComponentProps = {
  variant: keyof typeof typographyVariant;
  textColor: string;
  spacing?: SpacingType;
  fontSize?: number;
  fontWeight?: CSSProperties['fontWeight'];
  textAlign?: CSSProperties['textAlign'];
  lineHeight?: CSSProperties['lineHeight'];
};

const StyledDynamicTypographyComponent = styled.span.withConfig({
  shouldForwardProp: (propName, target) => {
    if (typeof target === 'string') {
      return isPropValid(propName);
    }
    return true;
  },
})<StyledDynamicComponentProps>`
  margin: unset;
  font-size: ${({ variant, fontSize }) => (fontSize ? fontSize : typographyVariant[variant].fontSize)}rem;
  font-weight: ${({ fontWeight, variant }) =>
    fontWeight ? fontWeight : typographyVariant[variant].fontWeight};
  color: ${({ textColor }) => textColor};
  font-family: ${({ variant }) => typographyVariant[variant].fontFamily}, serif;
  ${({ lineHeight }) => lineHeight && `line-height: ${lineHeight}`};
  ${({ textAlign }) => textAlign && `text-align: ${textAlign}`};
  ${({ spacing }) => spacing && spacingHelper(spacing)};
`;

type DynamicTypography = { tag: TagType; children: ReactNode } & StyledDynamicComponentProps;

const DynamicTypography = (props: DynamicTypography) => {
  const { children, tag, variant, textColor, textAlign, fontWeight, lineHeight, spacing, ...rest } = props;

  return (
    <StyledDynamicTypographyComponent
      {...rest}
      as={tag}
      variant={variant}
      textColor={textColor}
      fontWeight={fontWeight}
      lineHeight={lineHeight}
      textAlign={textAlign}
      spacing={spacing}
    >
      {children}
    </StyledDynamicTypographyComponent>
  );
};
